import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { TEnvironment } from '../Types/channel.types';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Button from 'Components/DesignSystem/Button/Button';
import { SidePanelTabConfig } from '../../../../types/DesignSystem/SidePanel';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import AppSettings from 'Settings/AppSettings';
import { EnvironmentAPIResponse } from '../Types/env.types';
import ConfirmDialog from '../Components/ConfirmDialog';
import { AppContext } from 'Context/AppContext';
import ConfigTab, { errorPath } from './ConfigTab';
import {
  ConfigField as ConfigFieldDef,
  ConfigTab as ConfigTabDef,
  ConfigValue,
  EnvironmentContext,
  EnvironmentFormState,
} from './config/adminConfig.types';
import { applyDependencies, makeLookup, sortedTabs, writeValue } from './config/configRules';
import { EnvironmentChannelResponse, changedPaths, fromApi, toPayload } from './config/configValueMapper';
import { tabOfError, validateAll, validateValue } from './config/configValidation';
import { useEnvironmentAdminConfig } from './config/useEnvironmentAdminConfig';

interface EnvironmentSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: TEnvironment | null;
  onSave?: (data: EnvironmentAPIResponse) => Promise<void> | void;
  isLoading?: boolean;
  selectedEnvironmentId: string;
  selectedEnvironmentName: string;
  allEnvironmentList: string[];
  hasProductionEnv: boolean;
}

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

interface PendingConfirm {
  tab: ConfigTabDef;
  field: ConfigFieldDef;
  value: ConfigValue;
}

/**
 * Manage Parties › Edit Environment. Since AB#14917 every tab, field, default, rule and message
 * comes from the admin config (`GET /channel/admin-config`), so adding a setting or a whole
 * application is a config change only. The backend validates saves against the same config.
 */
const EnvironmentSidePanel: React.FC<EnvironmentSidePanelProps> = ({
  isOpen,
  onClose,
  editData,
  onSave,
  isLoading = false,
  selectedEnvironmentId,
  selectedEnvironmentName,
  allEnvironmentList,
  hasProductionEnv
}) => {
  const { config, loading: isFetchingConfig, error: configError, reload: reloadConfig } = useEnvironmentAdminConfig(isOpen);
  const tabs = useMemo(() => (config ? sortedTabs(config) : []), [config]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [isFetchingChannel, setIsFetchingChannel] = useState(false);
  const [channelResponse, setChannelResponse] = useState<EnvironmentChannelResponse | null>(null);
  const [originalState, setOriginalState] = useState<EnvironmentFormState | null>(null);
  const [formState, setFormState] = useState<EnvironmentFormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [showUnSavedChangesDialog, setShowUnSavedChangesDialog] = useState(false);
  const appContext = useContext(AppContext);
  // Read through a ref inside submit: registering the save handler below updates AppContext,
  // so depending on appContext would re-create submit → re-register → update → … forever.
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const responseValues = useMemo<Record<string, ConfigValue>>(
    () => ({ ...(channelResponse?.basic_info ?? {}) }),
    [channelResponse],
  );
  const ctx = useMemo<EnvironmentContext>(() => ({
    responseValues,
    existingEnvironmentNames: allEnvironmentList,
    currentEnvironmentName: selectedEnvironmentName,
    hasProductionEnv,
    isProduction: originalState?.flat.is_prod === true,
  }), [responseValues, allEnvironmentList, selectedEnvironmentName, hasProductionEnv, originalState]);

  useEffect(() => {
    if (tabs.length && !tabs.some((tab) => tab.id === activeTab)) setActiveTab(tabs[0].id);
  }, [tabs, activeTab]);

  const fetchChannelData = async () => {
    setIsFetchingChannel(true);
    try {
      const response = await ServiceWrapper.doGet(
        api_base_url + `/channel/${selectedEnvironmentId}`,
        null,
        null,
        AuthManager.getGmsToken()
      );
      if (!response.errorfound && response.data && response.data.data) {
        setChannelResponse(response.data.data as EnvironmentChannelResponse);
      } else {
        toast.error(response.message || "Failed to fetch environment data")
        onClose();
        console.error('Error fetching channel data:', response.errorbj);
      }
    } catch (error) {
      onClose();
      console.error('Failed to fetch environment data:', error);
      toast.error('Failed to fetch environment data');
    } finally {
      setIsFetchingChannel(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedEnvironmentId) {
      appContext?.setEditInProgress(true);
      fetchChannelData();
    }
    return () => {
      appContext?.setEditInProgress(false);
      appContext?.setEditSaveHandler(null);
      appContext?.setEditCancelHandler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedEnvironmentId]);

  // Working values: every key the config defines, stored value or default.
  useEffect(() => {
    if (config && channelResponse) {
      const state = fromApi(channelResponse, config);
      setOriginalState(state);
      setFormState(state);
      setErrors({});
    }
  }, [config, channelResponse]);

  const changedFieldsCount = useMemo(
    () => (originalState && formState ? changedPaths(originalState, formState).length : 0),
    [originalState, formState],
  );

  const commitChange = (tab: ConfigTabDef, field: ConfigFieldDef, value: ConfigValue) => {
    if (!formState) return;
    const next = applyDependencies(writeValue(formState, tab, field, value), tab, field, value);
    const fieldError = validateValue(field, value, makeLookup(next, tab, responseValues), ctx);
    const path = errorPath(tab, field);
    setFormState(next);
    setErrors((current) => {
      const updated = Object.fromEntries(
        Object.entries(current).filter(([key]) => key !== path && !key.startsWith(`${path}.`)),
      );
      if (fieldError && field.type !== 'group' && field.type !== 'objectList') updated[path] = fieldError;
      return updated;
    });
  };

  const handleFieldChange = (tab: ConfigTabDef, field: ConfigFieldDef, value: ConfigValue) => {
    if (!formState) return;
    if (field.confirmOnChange && value === field.confirmOnChange.to) {
      setPendingConfirm({ tab, field, value });
      return;
    }
    if (field.requiresFields?.length && value === true) {
      const lookup = makeLookup(formState, tab, responseValues);
      const missing = field.requiresFields.some((key) => {
        const { value: required } = lookup(key);
        const requiredField = config?.tabs.flatMap((t) => t.sections.flatMap((s) => s.fields)).find((f) => (f.dbKey ?? f.key) === key);
        return required === undefined || required === null || String(required).trim() === '' ||
          (requiredField?.emptyValues ?? []).includes(required);
      });
      if (missing) {
        toast.error(field.requiresFieldsMessage ?? 'Please complete mandatory fields before enabling');
        return;
      }
    }
    commitChange(tab, field, value);
  };

  const handleFlatChange = (values: Record<string, ConfigValue>) => {
    setFormState((prev) => (prev ? { ...prev, flat: { ...prev.flat, ...values } } : prev));
    setErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !(key in values))));
  };

  const handleFieldBlur = (tab: ConfigTabDef, field: ConfigFieldDef) => {
    if (!formState) return;
    const path = errorPath(tab, field);
    const value = tab.apiSection ? formState.flat[field.dbKey ?? field.key] : formState.applications[tab.id]?.[field.key];
    const fieldError = validateValue(field, value, makeLookup(formState, tab, responseValues), ctx);
    setErrors((current) => {
      const updated = { ...current };
      if (fieldError) updated[path] = fieldError;
      else delete updated[path];
      return updated;
    });
  };

  // Latest values for submit: it is registered once as the left-menu save handler (AppContext), and
  // any dependency that changes when AppContext updates would re-register it in a render loop.
  const latestRef = useRef({ config, formState, ctx, configError, responseValues });
  latestRef.current = { config, formState, ctx, configError, responseValues };

  /** Validate, build the full payload and hand it to the caller. Returns whether it saved. */
  const submit = useCallback(async (): Promise<boolean> => {
    const { config, formState, ctx, configError, responseValues } = latestRef.current;
    if (!config || !formState) {
      toast.error(configError ? 'The environment settings configuration could not be loaded' : 'Environment data is still loading');
      return false;
    }
    const validationErrors = validateAll(formState, config, ctx);
    setErrors(validationErrors);
    const messages = Object.values(validationErrors);
    if (messages.length) {
      const firstTab = tabOfError(config, Object.keys(validationErrors)[0]);
      if (firstTab) setActiveTab(firstTab.id);
      /* § 116: headline -> `title`, the errors -> `description`. The DS
         description is one line, so they are middot-separated. */
      toast.error('Please fix the following errors:', {
        description: Array.from(new Set(messages)).join(' · '),
        duration: 5000,
      });
      return false;
    }
    const channelKey = String(responseValues.channel_key ?? '');
    const payload = toPayload(formState, config, channelKey) as unknown as EnvironmentAPIResponse;
    await onSaveRef.current?.(payload);
    const context = appContextRef.current;
    if (channelKey === context?.channelKey) {
      context?.setEnableConfigure(payload.guide.enable_configure === true);
      context?.setEnvPlatform?.(String(payload.basic_info?.platform ?? ''));
    }
    return true;
  }, []);

  const handleSaveForNav = useCallback(async (): Promise<boolean> => {
    try {
      const saved = await submit();
      if (saved) onCloseRef.current();
      return saved;
    } catch (error) {
      console.error('Error saving environment:', error);
      return false;
    }
  }, [submit]);

  // Register AppContext handlers for left menu unsaved changes modal
  useEffect(() => {
    if (isOpen) {
      appContext?.setEditSaveHandler(() => () => handleSaveForNav());
      appContext?.setEditCancelHandler(() => () => {
        onCloseRef.current();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, handleSaveForNav]);

  const handleSave = async () => {
    try {
      if (await submit()) onClose();
    } catch (error) {
      console.error('Error saving environment:', error);
    }
  };

  const handleClose = () => {
    if (changedFieldsCount > 0) {
      setShowUnSavedChangesDialog(true);
      return;
    }
    onClose();
  };

  const panelTabs: SidePanelTabConfig<string>[] = tabs.map((tab) => ({ id: tab.id, label: tab.label }));
  const currentTab = tabs.find((tab) => tab.id === activeTab);
  const title = editData
    ? (config?.title ?? 'Edit Environment: {{name}}').replace('{{name}}', editData?.name ?? '')
    : 'Add Environment';

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={handleClose}
        onSave={handleSave}
        title={title}
        tabs={panelTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isLoading={isLoading || isFetchingChannel || isFetchingConfig}
      >
        {configError && !config && (
          <div className="tw-space-y-3" data-testid="environment-config-error">
            <p className="tw-text-sm tw-text-tone-danger-ink">{configError}</p>
            <Button variant="secondary" onClick={() => void reloadConfig()} data-testid="environment-config-retry">
              Retry
            </Button>
          </div>
        )}
        {currentTab && formState && (
          <ConfigTab
            tab={currentTab}
            state={formState}
            ctx={ctx}
            errors={errors}
            onFieldChange={handleFieldChange}
            onFlatChange={handleFlatChange}
            onFieldBlur={handleFieldBlur}
          />
        )}
      </SidePanel>
      <ConfirmDialog
        isOpen={showUnSavedChangesDialog}
        title="Discard changes?"
        message="You have unsaved changes. Discard them?"
        confirmText="Discard"
        onConfirm={() => {
          setShowUnSavedChangesDialog(false);
          onClose();
        }}
        onCancel={() => setShowUnSavedChangesDialog(false)}
        data-testid="environment-discard-changes-confirm"
      />
      {/* One dialog for every `confirmOnChange` in the config (disable apps / ARS / Guide). */}
      <ConfirmDialog
        isOpen={!!pendingConfirm}
        title={pendingConfirm?.field.confirmOnChange?.title ?? ''}
        message={<span className="tw-whitespace-pre-line">{pendingConfirm?.field.confirmOnChange?.message}</span>}
        confirmText={pendingConfirm?.field.confirmOnChange?.confirmLabel ?? 'Continue'}
        onConfirm={() => {
          if (pendingConfirm) commitChange(pendingConfirm.tab, pendingConfirm.field, pendingConfirm.value);
          setPendingConfirm(null);
        }}
        onCancel={() => setPendingConfirm(null)}
        data-testid={pendingConfirm?.field.confirmOnChange?.testId ?? 'environment-setting-confirm'}
      />
    </>
  );
};

export default EnvironmentSidePanel;

